import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Sparkles, RefreshCw, Loader2, Info, ShieldAlert } from "lucide-react";

const SEVERITY_PILL = {
  high: "bg-red-100 text-red-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-emerald-100 text-emerald-700",
};

const SEVERITY_BAR = {
  high: "bg-red-500",
  medium: "bg-amber-500",
  low: "bg-emerald-500",
};

const OVERALL_STYLES = {
  high: "border-red-200 bg-red-50 text-red-700",
  medium: "border-amber-200 bg-amber-50 text-amber-700",
  low: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

function CategoryRow({ c }) {
  const isEstimate = c.data_source === "estimated_placeholder";
  return (
    <div
      data-testid={`risk-row-${c.key}`}
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:border-slate-300 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-slate-900">{c.label}</h4>
            {isEstimate && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-500">
                      <Info className="h-3 w-3" /> Estimated
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>No live data source — inferred from location.</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{c.summary}</p>
        </div>
        <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${SEVERITY_PILL[c.severity] || "bg-slate-100 text-slate-600"}`}>
          {c.severity}
        </span>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
          <div
            className={`h-full rounded-full ${SEVERITY_BAR[c.severity] || "bg-slate-400"}`}
            style={{ width: `${Math.min(100, Math.max(0, c.score || 0))}%` }}
          />
        </div>
        <span className="text-xs font-semibold tabular-nums text-slate-700">{c.score}</span>
      </div>
    </div>
  );
}

export default function RiskTab({ deal, onRun, running }) {
  const risk = deal?.risk_analysis;

  if (running && !risk) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2 text-sm font-medium text-indigo-600">
          <Loader2 className="h-4 w-4 animate-spin" /> AI is scoring this deal's risk…
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-slate-200" />
          ))}
        </div>
      </div>
    );
  }

  if (!risk) {
    return (
      <div
        data-testid="risk-empty"
        className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center"
      >
        <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
          <ShieldAlert className="h-6 w-6" />
        </div>
        <h3 className="text-xl font-semibold text-slate-900">Market Risk Score</h3>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
          10 risk categories — 7 deterministic (financial), 3 AI-estimated (flood/crime/market).
        </p>
        <Button
          onClick={onRun}
          data-testid="risk-tab-run"
          className="mt-6 bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
        >
          <Sparkles className="mr-2 h-4 w-4" /> Run Risk Analysis
        </Button>
      </div>
    );
  }

  const financial = (risk.categories || []).filter((c) => c.data_source === "deterministic");
  const external = (risk.categories || []).filter((c) => c.data_source === "estimated_placeholder");

  return (
    <div className="space-y-6" data-testid="risk-tab-content">
      <div className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-6 shadow-sm sm:p-8 ${OVERALL_STYLES[risk.overall_risk] || "border-slate-200 bg-white"}`}>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70">Overall risk</div>
          <div className="mt-1 text-3xl font-bold tracking-tight capitalize" data-testid="risk-overall">
            {risk.overall_risk || "—"}
          </div>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed">{risk.overall_summary}</p>
        </div>
        <Button
          variant="outline"
          onClick={onRun}
          disabled={running}
          data-testid="risk-tab-regenerate"
          className="border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
        >
          {running ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Regenerating…
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" /> Regenerate
            </>
          )}
        </Button>
      </div>

      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500">Financial risks</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {financial.map((c) => (
            <CategoryRow key={c.key} c={c} />
          ))}
        </div>
      </section>

      <section>
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
          External risks
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-slate-500">
            Estimated — no live data source
          </span>
        </h3>
        <div className="grid gap-3 sm:grid-cols-3">
          {external.map((c) => (
            <CategoryRow key={c.key} c={c} />
          ))}
        </div>
      </section>

      {risk.ai_status === "fallback" && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          AI service unavailable — showing baseline analysis.
        </div>
      )}
    </div>
  );
}
